import { getPlainTextFromDoc } from "@compass/shared/tiptap/plaintext";
import { DownloadAllButton, FileAttachment, ProseDocument } from "components/elements";
import { CopyShareButton } from "components/elements/CopyShareButton";
import { useUserTimezone } from "hooks/useUserTimezone";
import { cn } from "lib/cn";
import { formatISODateRelative } from "lib/formatISODate";
import { formatFullTimestamp, formatRelativeTime } from "lib/relativeTime";
import {
    ApiThreadComment,
    ThreadCommentFeatures,
    ThreadContext,
    contextSurface,
    getContextFeatures,
} from "lib/threadCommentUtils";
import type { ClientCurrentUser } from "@compass/shared/types";
import { AuthorAvatar, AuthorName } from "./AuthorChip";
import { ThreadCommentActions } from "./ThreadCommentActions";

export interface UnifiedThreadCommentProps {
    /** The comment data from the API */
    comment: ApiThreadComment;
    /** Thread context (social, discussion, blog, vendor) */
    context: ThreadContext;
    /** Current logged-in user */
    currentUser?: ClientCurrentUser;
    /** Whether the user can edit this comment */
    canEdit?: boolean;
    /** Callback when edit is clicked */
    onEdit?: () => void;
    /** Callback when delete is clicked */
    onDelete?: () => void;
    /** Callback when reply is clicked */
    onReply?: () => void;
    /** Override default features for this context */
    features?: Partial<ThreadCommentFeatures>;
    /** Additional CSS classes */
    className?: string;
    /** Custom share URL (if different from default) */
    shareUrl?: string;
    /** Additional metadata to render (e.g., reactions for social) */
    metadataSlot?: React.ReactNode;
    /** Slot for rendering nested replies below the comment */
    repliesSlot?: React.ReactNode;
    /** Custom ID prefix for anchor links */
    idPrefix?: string;
}

/**
 * Unified Thread Comment Component
 *
 * This component handles rendering of comments across all thread contexts:
 * - Social posts
 * - Discussions
 * - Blog posts
 * - Vendor feedback
 * - Multimedia
 *
 * Features are automatically configured based on context but can be overridden.
 */
export function UnifiedThreadComment({
    comment,
    context,
    currentUser,
    canEdit: canEditProp,
    onEdit,
    onDelete,
    onReply,
    features: featureOverrides,
    className = "",
    shareUrl,
    metadataSlot,
    repliesSlot,
    idPrefix = "comment",
}: UnifiedThreadCommentProps) {
    const timeZone = useUserTimezone();
    const features = { ...getContextFeatures(context), ...featureOverrides };

    // Determine if user can edit — respect explicit override, otherwise trust
    // the server-computed userCanEdit on the comment.
    const canEdit = Boolean(canEditProp ?? comment.userCanEdit);

    // Avatar seed: the author id when we have one, else the comment id so
    // an anonymous comment's fallback avatar cannot be correlated to a user.
    const avatarSeed = comment.authorId ?? comment.id;

    // Format timestamps
    const relativeTime =
        context === "discussion"
            ? formatRelativeTime(comment.createdAt, timeZone)
            : formatISODateRelative(comment.createdAt);
    const fullTimestamp = formatFullTimestamp(comment.createdAt, timeZone);

    return (
        <div id={`${idPrefix}-${comment.id}`} className={cn("flex flex-col gap-2", className)}>
            <div className="flex items-start justify-between gap-3">
                {/* Avatar and Content */}
                <div className="flex w-full items-start justify-start gap-2">
                    <AuthorAvatar
                        author={comment.author}
                        surface={contextSurface(context)}
                        className="h-[40px] w-[40px] bg-gray-200"
                        seed={avatarSeed}
                        link={features.profileLinks}
                        tooltip
                    />

                    {/* Comment Content */}
                    <div className="flex min-w-0 flex-1 flex-col gap-1">
                        {/* Author and timestamp header */}
                        <div className="flex items-center gap-2 text-sm">
                            <AuthorName
                                author={comment.author}
                                surface={contextSurface(context)}
                                className="font-semibold text-gray-800"
                                link={features.profileLinks}
                            />

                            {/* PRTL-1348: the org this author participates
                                under in this thread's context — only set on
                                org-scoped surfaces (discussions). */}
                            {comment.author.type === "identified" &&
                                comment.author.organizationName && (
                                    <span className="max-w-[160px] truncate rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500">
                                        {comment.author.organizationName}
                                    </span>
                                )}

                            <span className="text-gray-400">·</span>

                            {features.shareButton && shareUrl ? (
                                <a
                                    href={shareUrl}
                                    className="text-gray-600 hover:underline"
                                    title={fullTimestamp}
                                >
                                    {relativeTime}
                                </a>
                            ) : (
                                <span className="text-gray-600" title={fullTimestamp}>
                                    {relativeTime}
                                </span>
                            )}

                            {features.editedIndicator && comment.isEdited && (
                                <span className="text-xs text-gray-400 italic">(Edited)</span>
                            )}

                            {features.shareButton && shareUrl && (
                                <CopyShareButton
                                    textToCopy={shareUrl}
                                    className="min-w-max border-none shadow-none"
                                />
                            )}
                        </div>

                        {/* Comment body */}
                        {getPlainTextFromDoc(comment.contentJson).trim().length > 0 && (
                            <div className="prose prose-sm max-w-none">
                                <ProseDocument doc={comment.contentJson} />
                            </div>
                        )}

                        {/* Attachments */}
                        {features.attachments && comment.attachments.length > 0 && (
                            <div className="mt-2 flex flex-col gap-2">
                                {comment.attachments.map((attachment: any) => (
                                    <FileAttachment file={attachment} key={attachment.id} />
                                ))}
                                <DownloadAllButton
                                    fileIds={comment.attachments.map((a: { id: string }) => a.id)}
                                    archiveName="message-attachments"
                                />
                            </div>
                        )}

                        {/* Metadata slot (reactions, etc.) */}
                        {metadataSlot}

                        {/* Reply button */}
                        {features.replies && onReply && (
                            <button
                                type="button"
                                onClick={onReply}
                                className="mt-1 cursor-pointer text-xs font-medium text-gray-500 hover:text-gray-700"
                            >
                                Reply
                            </button>
                        )}
                    </div>
                </div>

                {/* Actions menu */}
                <ThreadCommentActions
                    canEdit={canEdit}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    className="shrink-0"
                />
            </div>

            {/* Nested replies */}
            {repliesSlot}
        </div>
    );
}
